import {
  createPlugin,
  EventDef,
  EventInstance,
  EventStore,
  DateProfile,
} from '@fullcalendar/react';
import dayjs from 'dayjs';
import {
  OperationalPeriod,
  useCurrentOperationalPeriod,
  usePeriods,
} from './hooks/incidentHooks';
import { isDateRangeInPeriod } from '../../utils/operationalPeriodUtils';
import EventCard from './EventCard';
import { eventTypes } from './hooks/incidentCalendarHooks';

interface SliceEventsProps {
  defs: Record<string, EventDef>;
  instances: Record<string, EventInstance>;
  period: OperationalPeriod;
}

type Segment = {
  title: string;
  attendees: string[];
  purpose: string;
  location: string;
  incidentId: string;
  start?: string;
  end?: string;
  eventClickCallback: () => void;
  color: string;
  eventTypeLabel: string;
  teamsJoinUrl?: string | null;
};

const sliceEvents = ({
  defs,
  instances,
  period,
}: SliceEventsProps): Segment[] => {
  return Object.keys(defs)
    .map((defId) => {
      const def = defs[defId];
      const instance = Object.values(instances).find(
        (inst) => inst.defId === defId
      );

      if (!instance) return null;

      const eventStart = dayjs(instance.range.start);
      const eventEnd = dayjs(instance.range.end);

      // Filter events outside the period range
      if (!isDateRangeInPeriod(eventStart, eventEnd, period)) {
        return null;
      }

      return {
        title: def.title,
        attendees: def.extendedProps.attendees,
        purpose: def.extendedProps.purpose,
        location: def.extendedProps.location,
        incidentId: def.extendedProps.incident_id,
        start: eventStart.toISOString(),
        end: eventEnd.toISOString(),
        eventClickCallback: def.extendedProps.eventClickCallback,
        teamsJoinUrl: def.extendedProps.teamsJoinUrl,
        color: def.ui.backgroundColor,
        eventTypeLabel:
          eventTypes.find((type) => type.code === def.ui.backgroundColor)
            ?.title || 'Other',
      };
    })
    .filter((segment) => segment !== null); // Remove null values
};

function PeriodHeader({ period }: { period: OperationalPeriod }) {
  return (
    <h5>
      Operational Period {period.period_number}:{' '}
      {dayjs(period.start_date_time).format('MM/DD/YYYY HH:mm')} -{' '}
      {dayjs(period.end_date_time || period.anticipated_end_date_time).format(
        'MM/DD/YYYY HH:mm'
      )}
    </h5>
  );
}

type OperationalPeriodViewProps = {
  eventStore: EventStore;
  dateProfile: DateProfile;
};

function OperationalPeriodView(props: OperationalPeriodViewProps) {
  const { data: definedPeriods } = usePeriods();
  const { data: currentPeriod } = useCurrentOperationalPeriod();
  const currentPeriodNumber = currentPeriod?.period_number ?? 0;

  const {
    eventStore: { defs, instances },
    dateProfile: { currentRange },
  } = props;

  // Only periods that have started; the future period holds the planned window but is not underway yet
  const periods = definedPeriods?.filter((period) => {
    const periodNumber = period.period_number ?? 0;
    if (periodNumber > currentPeriodNumber) {
      return false;
    }
    if (
      dayjs(period.start_date_time).isBefore(currentRange.end) &&
      dayjs(period.end_date_time || period.anticipated_end_date_time).isAfter(
        currentRange.start
      )
    ) {
      return true;
    }
    return false;
  });

  return (
    <>
      {periods && periods.length > 0 ? (
        periods.map((period) => {
          const events = sliceEvents({ defs, instances, period });
          return (
            <div key={period.id} className="mb-3">
              {events.length > 0 ? (
                <>
                  <PeriodHeader period={period} />
                  {events.map((seg: any, index: any) => (
                    <EventCard
                      key={index}
                      seg={seg}
                      eventClickCallback={seg.eventClickCallback}
                    />
                  ))}
                </>
              ) : null}
            </div>
          );
        })
      ) : (
        <p>No operational periods available.</p>
      )}
    </>
  );
}

export default createPlugin({
  views: {
    byPeriod: {
      buttonText: 'By Period',
      component: OperationalPeriodView,
      duration: { years: 1 },
      titleFormat: { year: 'numeric' },
    },
  },
});
